import { FC, Dispatch, SetStateAction, useCallback } from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { JobPreview, JobseekerPostActions, PostSEO } from '@carcareers/components';
import { JobSearchPost } from '@carcareers/pages';
import { PostStatus } from '@libs/types';

interface JobseekerPostViewProps {
  post: JobSearchPost | null;
  prevPost: JobSearchPost | null;
  setPosts: Dispatch<SetStateAction<Record<string, JobSearchPost>>>;
  setSelectedPost: Dispatch<SetStateAction<JobSearchPost | null>>;
}

export const JobseekerPostView: FC<JobseekerPostViewProps> = ({ post, prevPost, setPosts, setSelectedPost }) => {
  const handleApplySuccess = useCallback(
    (postId: number) => {
      setPosts((prev) => {
        const updatedPosts = { ...prev };

        Object.keys(updatedPosts).forEach((key) => {
          if (updatedPosts[key].id === postId) {
            updatedPosts[key] = {
              ...updatedPosts[key],
              applied: !updatedPosts[key].applied,
            };
          }
        });

        return updatedPosts;
      });

      if (post?.id === postId) {
        setSelectedPost((prev) => (prev ? { ...prev, applied: !prev.applied } : null));
      }
    },
    [post?.id, setPosts, setSelectedPost],
  );

  const handleUnapplySuccess = useCallback(
    (postId: number) => {
      setPosts((prev) => {
        const updatedPosts = { ...prev };

        Object.keys(updatedPosts).forEach((key) => {
          if (updatedPosts[key].id === postId) {
            updatedPosts[key] = {
              ...updatedPosts[key],
              applied: false,
            };
          }
        });

        return updatedPosts;
      });

      if (post?.id === postId) {
        setSelectedPost((prev) => (prev ? { ...prev, applied: false } : null));
      }
    },
    [post?.id, setPosts, setSelectedPost],
  );

  const handleSaveSuccess = useCallback(
    (postId: number) => {
      setPosts((prev) => {
        const updatedPosts = { ...prev };

        Object.keys(updatedPosts).forEach((key) => {
          if (updatedPosts[key].id === postId) {
            updatedPosts[key] = {
              ...updatedPosts[key],
              saved: !updatedPosts[key].saved,
            };
          }
        });

        return updatedPosts;
      });

      if (post?.id === postId) {
        setSelectedPost((prev) => (prev ? { ...prev, saved: !prev.saved } : null));
      }
    },
    [post?.id, setPosts, setSelectedPost],
  );

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        position: 'relative',
        height: '100%',
        maxHeight: { xs: 'calc(100vh - 160px)', sm: 'calc(100vh - 136px)' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PostSEO post={post} />
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }} onClick={() => setSelectedPost(null)}>
            <ArrowBackIcon />
          </IconButton>
          <JobseekerPostActions
            post={post}
            applied={post?.applied}
            saved={post?.saved}
            onApplySuccess={handleApplySuccess}
            onUnapplySuccess={handleUnapplySuccess}
            onSaveSuccess={handleSaveSuccess}
            disabled={post?.status !== PostStatus.ACTIVE}
          />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0,0,0,0.5)',
          },
        }}
      >
        {post?.job ? (
          <JobPreview data={post.job} inactive={post.status !== PostStatus.ACTIVE} descriptionFillHeight linkToRooftop />
        ) : prevPost?.job ? (
          <JobPreview data={prevPost.job} inactive={prevPost.status !== PostStatus.ACTIVE} descriptionFillHeight linkToRooftop />
        ) : (
          <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default JobseekerPostView;
